/**
 * Client-side rate limiter for authentication
 * Prevents excessive login attempts
 */

const RATE_LIMIT_WINDOW = 15 * 60 * 1000; // 15 minutes in ms
const MAX_ATTEMPTS = 5;

class RateLimiter {
  constructor() {
    this.attempts = this.loadAttempts();
  }

  loadAttempts() {
    try {
      const stored = localStorage.getItem('pacarion_login_attempts');
      if (!stored) return {};

      const attempts = JSON.parse(stored);
      const now = Date.now();

      // Clean up expired entries
      Object.keys(attempts).forEach(key => {
        if (now - attempts[key].firstAttempt > RATE_LIMIT_WINDOW) {
          delete attempts[key];
        }
      });

      return attempts;
    } catch {
      return {};
    }
  }

  saveAttempts() {
    try {
      localStorage.setItem('pacarion_login_attempts', JSON.stringify(this.attempts));
    } catch (error) {
      console.error('Failed to save rate limit data:', error);
    }
  }

  checkLimit(identifier) {
    const now = Date.now();
    const attempt = this.attempts[identifier];

    if (!attempt) {
      return { allowed: true, remaining: MAX_ATTEMPTS };
    }

    // Reset if window has passed
    if (now - attempt.firstAttempt > RATE_LIMIT_WINDOW) {
      delete this.attempts[identifier];
      this.saveAttempts();
      return { allowed: true, remaining: MAX_ATTEMPTS };
    }

    // Check if limit exceeded
    if (attempt.count >= MAX_ATTEMPTS) {
      const timeLeft = Math.ceil((RATE_LIMIT_WINDOW - (now - attempt.firstAttempt)) / 60000);
      return {
        allowed: false,
        remaining: 0,
        retryAfter: timeLeft,
        message: `Too many login attempts. Please try again in ${timeLeft} minute${timeLeft !== 1 ? 's' : ''}.`
      };
    }

    return {
      allowed: true,
      remaining: MAX_ATTEMPTS - attempt.count
    };
  }

  recordAttempt(identifier, success = false) {
    const now = Date.now();

    if (success) {
      // Clear attempts on successful login
      delete this.attempts[identifier];
      this.saveAttempts();
      return;
    }

    // Record failed attempt
    if (!this.attempts[identifier]) {
      this.attempts[identifier] = {
        count: 1,
        firstAttempt: now
      };
    } else {
      this.attempts[identifier].count++;
    }

    this.saveAttempts();
  }

  resetAttempts(identifier) {
    delete this.attempts[identifier];
    this.saveAttempts();
  }
}

// Export singleton instance
export const rateLimiter = new RateLimiter();
